/* eslint-disable @next/next/no-img-element */
import React, { useRef, useState, useEffect } from 'react'
import useAmplitude from '../../hooks/useAmplitude'
import useApi from '../../hooks/useApi'
import {
  VIDEO_INPUT_CHANGED,
  ANALYZE_VIDEO_CLICKED,
  ANALYZE_VIDEO_FINISHED,
  ANALYZE_VIDEO_FAILED,
} from '../../helpers/events'
import CTAButton from '../CTAButton'

export default function VideoAnalyzer() {
  const [validForm, setValidForm] = useState(false)
  const trackedVideoUrl = useRef()
  const videoRef = useRef()
  const additionalVideoRef = useRef()
  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const { logEvent } = useAmplitude()
  const { analyzeVideo } = useApi()
  const [analyzedVideos, setAnalyzedVideos] = useState([])
  const [showNewVideoInput, setShowNewVideoInput] = useState(false)

  useEffect(() => {
    if (!analyzedVideos.length) {
      return
    }
    document
      .getElementById(`video-${analyzedVideos.length - 1}`)
      .scrollIntoView({ behavior: 'smooth' })
  }, [analyzedVideos])

  const onVideoUrlChanged = (ref) => {
    setError(false)
    setValidForm(ref.current.value)

    if (!trackedVideoUrl.current) {
      return
    }
    trackedVideoUrl.current = true
    logEvent(VIDEO_INPUT_CHANGED, { source: 'video-analyzer' })
  }

  const onAnalyzeVideo = async (ref) => {
    if (!ref.current.reportValidity()) {
      return
    }
    setLoading(true)
    const payload = { url: ref.current.value }
    logEvent(ANALYZE_VIDEO_CLICKED, payload)

    try {
      const videoInfo = await analyzeVideo(payload)
      setAnalyzedVideos((old) => [...old, videoInfo])
      logEvent(ANALYZE_VIDEO_FINISHED, payload)
      setSuccess(true)
      ref.current.value = ''
      setTimeout(() => {
        setSuccess(false)
      }, 3000)
    } catch (err) {
      setError(
        'Something went wrong. Plese check if this video has an auto generated transcript.',
      )
      logEvent(ANALYZE_VIDEO_FAILED, { url: ref.current.value })
    } finally {
      setLoading(false)
    }
  }

  const highlightKeywords = (sentence, keywords) => {
    const regex = new RegExp(`(${(keywords || ['!?!?!?!']).join('|')})`, 'ig')

    return sentence.split(regex).map((part, partIndex) => {
      if (part.match(regex)) {
        return (
          <span
            key={`sentence-${partIndex}`}
            className="font-bold text-purple-500 underline">
            {part}
          </span>
        )
      }

      return <span key={`sentence-${partIndex}`}>{part}</span>
    })
  }

  const formatTime = (seconds) => {
    const date = new Date(null)
    date.setSeconds(seconds)

    return date.toISOString().substr(11, 8)
  }

  const formatNumber = (number) => {
    return new Intl.NumberFormat().format(number)
  }

  return (
    <div className="flex flex-col justify-center space-y-8">
      <div className="flex flex-col w-full px-8 py-12 mx-auto">
        <div className="flex flex-col items-center mb-4 lg:flex-row">
          <p className="flex-shrink-0 mb-2 mr-4 text-sm font-medium text-left text-gray-400 rounded lg:mg-0 focus:ring-4 focus:ring-purple-500 focus:ring-opacity-50 focus:ring-offset-2 focus:outline-none">
            Analyze this YouTube video:
          </p>
          <input
            required
            ref={videoRef}
            onChange={() => onVideoUrlChanged(videoRef)}
            type="text"
            className="flex-1 w-full px-5 py-3 text-sm placeholder-gray-600 border border-gray-300 rounded-full lg:text-2xl focus:ring-4 focus:border-purple-700 focus:ring-purple-500 focus:ring-opacity-50 focus:outline-none"
            placeholder="Enter Public Youtube Link"
          />
        </div>
        {error && <p className="mb-2 text-center text-red-500">{error}</p>}
        <CTAButton
          onClick={() => onAnalyzeVideo(videoRef)}
          disabled={!validForm || loading}
          loading={loading}
          success={success}
          text="Analyze Video"
          className="flex-1 w-full px-5 py-3 text-sm placeholder-gray-600 border border-gray-300 rounded-full lg:text-2xl focus:ring-4 focus:border-purple-700 focus:ring-purple-500 focus:ring-opacity-50 focus:outline-none"
        />
      </div>
      {analyzedVideos.map((video, index) => (
        <div
          id={`video-${index}`}
          className="px-12 py-12 bg-white rounded-3xl"
          key={index}>
          <h2 className="text-3xl font-bold text-gray-800 sm:text-4xl">
            {video.title}
          </h2>
          <div className="flex flex-col mt-8 mb-8 md:flex-row">
            <img
              className="rounded-3xl"
              width={500}
              height={200}
              src={video.thumbnail.url}
            />
            <div className="flex flex-col mt-8 ml-0 text-xl font-bold text-gray-400 md:mt-0 md:ml-4">
              <p className="text-xl text-gray-400 h-100">
                {formatTime(video.lengthSeconds)}
              </p>
              <p>
                <span className="text-gray-600">
                  {formatNumber(video.viewCount)}
                </span>{' '}
                Views
              </p>
              <p>
                <span className="text-green-500">
                  {formatNumber(video.likes)}
                </span>{' '}
                Likes
              </p>
              <p>
                <span className="text-red-500">
                  {new Intl.NumberFormat().format(video.dislikes)}
                </span>{' '}
                Dislikes
              </p>
              <p>
                <span className="text-gray-600">
                  {formatNumber(Math.round(video.likes / video.dislikes))}
                </span>{' '}
                Likes per Dislike
              </p>
              <p>
                <span className="text-gray-600">
                  {formatNumber(Math.round(video.viewCount / video.likes))}
                </span>{' '}
                Views per Like
              </p>
            </div>
          </div>
          <h3 className="mt-12 text-2xl font-bold text-gray-800">
            Description
          </h3>
          <div className="mt-4 overflow-hidden text-gray-500">
            {video.description.split('\n').map((part, index) => (
              <span key={`part-${index}`}>
                {highlightKeywords(part, video.keywords)}
                <br />
              </span>
            ))}
          </div>
          <h3 className="mt-12 text-2xl font-bold text-gray-800">Keywords</h3>
          <div className="flex flex-wrap items-center w-full mt-4">
            {!video.keywords && (
              <p className="text-gray-400">This video has no keywords</p>
            )}
            {(video.keywords || []).map((keyword) => (
              <div
                className="px-4 py-2 mt-2 mr-2 bg-purple-500 rounded-full"
                key={keyword}>
                <p className="text-white">{keyword}</p>
              </div>
            ))}
          </div>
        </div>
      ))}
      {analyzedVideos.length > 0 && !showNewVideoInput && (
        <CTAButton
          onClick={() => setShowNewVideoInput(true)}
          text="Analyze another video"
        />
      )}
      {showNewVideoInput && (
        <div className="relative w-full">
          <input
            required
            ref={additionalVideoRef}
            onChange={() => onVideoUrlChanged(additionalVideoRef)}
            type="text"
            className="w-full px-5 py-3 text-xl placeholder-gray-600 border border-gray-300 rounded-full focus:ring-4 focus:border-purple-700 focus:ring-purple-500 focus:ring-opacity-50 focus:outline-none"
            placeholder="Enter Public Youtube Link"
          />
          <CTAButton
            text="Add"
            disabled={!validForm || loading}
            loading={loading}
            success={success}
            onClick={() => onAnalyzeVideo(additionalVideoRef)}
            className="absolute px-4 transition-all hover:bg-purple-800 top-2 right-2 bottom-2"
          />
        </div>
      )}
      {/* <button className="text-gray-800 underline">
        Export Analysis
      </button> */}
    </div>
  )
}
